import { useParams } from "react-router-dom";

import { useAdminPatient } from "../../../hooks/queries/admin/patients";
import {
  AdminBreadcrumb,
  ErrorMessage,
  PageContainer,
  PageHeader,
  PageIntro,
  PageTitle,
  PatientDetailsPanel,
} from "../shared";

/**
 * Patient details route (EN-5169 + EN-5170) — the page shell around the shared
 * `PatientDetailsPanel`.
 *
 * Retool navigated here with `openApp(..., { queryParams: { id } })` and read
 * `url.searchParams.id`; a path parameter makes a patient linkable the ordinary
 * way. A route rather than a drawer for the same reason as Staff Details
 * (EN-5150): the panel's audit trails and drill-downs need the global modal
 * slot, which a drawer would occupy.
 */
export function AdminPatientDetails() {
  const { patientId } = useParams<{ patientId: string }>();
  const parsedPatientId = Number(patientId);
  const isValidPatientId =
    Number.isFinite(parsedPatientId) && parsedPatientId > 0;

  const { patient } = useAdminPatient(
    isValidPatientId ? parsedPatientId : undefined
  );

  // Falls back to the id until the record loads, so the crumb and title never
  // blink through an empty string.
  const currentLabel = isValidPatientId
    ? `Patient ${parsedPatientId}`
    : "Patient details";
  const heading = patient?.hospitalPatientId
    ? `${currentLabel} · MRN ${patient.hospitalPatientId}`
    : currentLabel;

  document.title = `${currentLabel} - Admin`;

  return (
    <PageContainer>
      <PageIntro>
        <AdminBreadcrumb
          items={[
            { label: "Patients", to: "/admin/patients" },
            { label: currentLabel },
          ]}
        />
        <PageHeader>
          <PageTitle>{heading}</PageTitle>
        </PageHeader>
      </PageIntro>

      {isValidPatientId ? (
        <PatientDetailsPanel patientId={parsedPatientId} />
      ) : (
        <ErrorMessage>That patient id isn&apos;t valid.</ErrorMessage>
      )}
    </PageContainer>
  );
}
